<template>
  <DialogLayout v-model="isOpen" :title="modalTitle" width="900px">
    <AlertsAnimated :alerts="localAlerts" />

    <div class="pa-4">
      <div class="d-flex align-center justify-space-between">
        <div>
          <span>Task ID: {{ task.taskId }}</span>
        </div>
        <div>
          <span class="mr-4">
            Task Due Date:
            {{ formatDateSlashUTC(task.dueAt) }}
          </span>
        </div>
      </div>
      <div v-if="task?.description" class="mt-2">
        <span>Task Description: {{ task.description }}</span>
      </div>
    </div>

    <v-card v-if="checklistTasks.length > 0" outlined class="mb-5">
      <div class="checklist-header pa-4 grey lighten-3">
        <div class="d-flex justify-space-between align-center">
          <span class="font-weight-bold">Checklist Items</span>
          <span class="text-caption grey--text"
            >{{ completedCount }}/{{ totalCount }} completed</span
          >
        </div>
        <v-progress-linear
          :value="progressPercentage"
          color="success"
          height="4"
          class="mt-2"
        />
      </div>

      <div class="checklist-items">
        <div
          v-for="(item, index) in checklistTasks"
          :key="index"
          class="checklist-item"
          :class="{
            completed: item.completed,
            'border-bottom': index < checklistTasks.length - 1,
          }"
        >
          <div class="d-flex align-start pa-4">
            <v-row>
              <v-col cols="1">
                <v-checkbox
                  v-model="item.completed"
                  @change="updateTaskStatus(item, index)"
                  class="ma-0 pa-0 mr-4 mt-1"
                  color="success"
                  hide-details
                />
              </v-col>
              <v-col cols="7">
                <div
                  class="task-description flex-grow-1"
                  :class="{ 'completed-text': item.completed }"
                >
                  {{ item.description }}
                </div>
              </v-col>

              <v-col cols="4">
                <small align="end" class="overdue-text" v-if="item.required">
                  Required*
                </small>
                <v-text-field
                  v-model="item.comment"
                  placeholder="Comment..."
                  dense
                  outlined
                  hide-details
                  class="comment-field"
                  @change="updateTaskComment(item, index)"
                />
              </v-col>
            </v-row>
          </div>
        </div>
      </div>
    </v-card>
    <div v-if="isPhotoVideoRequired">
      <CardTaskAttachments
        :task-id="task.id"
        :initial-attachments="currentTaskFiles"
      />
    </div>

    <div v-if="checklistTasks.length === 0" class="text-center py-8">
      <v-icon size="64" color="grey lighten-2"
        >mdi-clipboard-list-outline</v-icon
      >
      <p class="mt-4 grey--text text-h6">No checklist items for this task</p>
    </div>
    <template #actions>
      <v-btn
        v-if="task.resolution != 'done'"
        color="success"
        :disabled="!hasAnyCompletedTasks"
        @click="completeTask"
        depressed
        large
        :loading="completing"
      >
        Complete Task
      </v-btn>
    </template>
  </DialogLayout>
</template>

<script>
import DialogLayout from "@layout/DialogLayout.vue";
import { UPDATE_TASK, GET_TASK } from "./graphql";
import { formatDateSlashUTC } from "@tod-ui/helpers/datetime";
import useUser from "@components/authentication/useUser";
import CardTaskAttachments from "./CardTaskAttachments.vue";
import AlertsAnimated from "@tod-ui/components/AlertsAnimated.vue";
import useAlerts from "@tod-ui/composables/useAlerts";

export default {
  name: "DialogChecklistModal",
  components: {
    DialogLayout,
    CardTaskAttachments,
    AlertsAnimated,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    task: {
      type: Object,
      required: false,
      default: null,
    },
  },
  setup() {
    const { defaultOrganization } = useUser();
    const { addLocalAlert, clearAllAlerts, localAlerts } = useAlerts();

    return {
      defaultOrganization,
      addLocalAlert,
      clearAllAlerts,
      localAlerts,
    };
  },
  data() {
    return {
      checklistTasks: [],
      updating: false,
      completing: false,
      taskFiles: [],
    };
  },
  computed: {
    isOpen: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
        if (!value) {
          this.$emit("close");
          this.clearAllAlerts();
        }
      },
    },
    modalTitle() {
      return this.task?.title || "Task Checklist";
    },
    hasAnyCompletedTasks() {
      return this.checklistTasks.some((task) => task.completed);
    },
    completedCount() {
      return this.checklistTasks.filter((task) => task.completed).length;
    },
    totalCount() {
      return this.checklistTasks.length;
    },
    progressPercentage() {
      return this.totalCount > 0
        ? (this.completedCount / this.totalCount) * 100
        : 0;
    },
    isPhotoVideoRequired() {
      return Boolean(this.task?.actions?.photoVideoRequired);
    },
    currentTaskFiles() {
      return this.taskFiles.length > 0
        ? this.taskFiles
        : this.task?.files || [];
    },
    // attachmentComponentKey() {
    //   return `attachment-${this.task?.id}-${
    //     this.currentTaskFiles.length
    //   }-${JSON.stringify(this.currentTaskFiles.map((f) => f.id))}`;
    // },
  },
  apollo: {
    taskFromQuery: {
      query: GET_TASK,
      variables() {
        return {
          id: this.task?.id,
        };
      },
      update(data) {
        if (data?.findTask?.files) {
          this.taskFiles = data.findTask.files;
        }
        return data?.findTask || null;
      },
      skip() {
        return !this.task?.id || !this.value || !this.isPhotoVideoRequired;
      },
      fetchPolicy: "cache-and-network",
    },
  },
  watch: {
    task: {
      handler(newTask, oldTask) {
        if (newTask) {
          const isDifferentTask = !oldTask || oldTask.id !== newTask.id;
          const isChecklistEmpty = this.checklistTasks.length === 0;

          if (isDifferentTask || isChecklistEmpty) {
            this.checklistTasks = this.initializeChecklistTasks();
            this.clearAllAlerts();
          }
        }
      },
      deep: true,
      immediate: true,
    },
    value: {
      handler(newVal) {
        if (newVal) {
          this.clearAllAlerts();
          this.taskFiles = [];
        }
      },
    },
  },
  methods: {
    initializeChecklistTasks() {
      if (
        this.task?.actions?.checklist &&
        Array.isArray(this.task.actions.checklist)
      ) {
        return this.task.actions.checklist.map((item) => ({
          id: item.id,
          description: item.description,
          completed: item.completed || false,
          required: item.required || false,
          comment: item.comment || "",
        }));
      }

      return [];
    },

    updateTaskStatus(item, index) {
      // this.$emit("task-updated", {
      //   taskId: this.task.id,
      //   checklistIndex: index,
      //   completed: item.completed,
      //   description: item.description,
      //   type: "local_update",
      // });
    },

    updateTaskComment(item, index) {
      this.$emit("task-updated", {
        taskId: this.task.id,
        checklistIndex: index,
        comment: item.comment,
        description: item.description,
        type: "local_update",
      });
    },

    async completeTask() {
      if (!this.task) return;

      this.clearAllAlerts();

      this.completing = true;

      try {
        const checklistActions = this.checklistTasks.map((item) => ({
          id: item.id,
          completed: item.completed,
          comment: item.comment,
        }));

        const allChecklistCompleted =
          this.checklistTasks.length > 0 &&
          this.checklistTasks.every((item) => item.completed);

        await this.$apollo.mutate({
          mutation: UPDATE_TASK,
          variables: {
            id: this.task.id,
            assigneeId: this.task.assignee.id,
            description: this.task.description,
            dueAt: this.task.dueAt,
            organizationId: this.task.organization.id,
            priority: this.task.priority,
            resolution: allChecklistCompleted ? "done" : this.task.resolution,
            taskTypeId: this.task.taskType.id,
            title: this.task.title,
            actions: {
              checklist: checklistActions,
            },
          },
        });

        this.$emit("task-updated", {
          type: "complete_task",
          taskId: this.task.id,
          checklist: this.checklistTasks,
          resolution: "completed",
        });

        this.addLocalAlert({
          type: "success",
          message: allChecklistCompleted
            ? "Task completed successfully!"
            : "Checklist completed successfully!",
        });
        setTimeout(() => {
          this.isOpen = false;
        }, 1500);
      } catch (error) {
        console.error("Error completing task:", error);

        if (error.graphQLErrors && error.graphQLErrors.length > 0) {
          error.graphQLErrors.forEach((graphQLError) => {
            this.addLocalAlert({
              type: "error",
              message: graphQLError.message,
            });
          });
        } else {
          this.addLocalAlert({
            type: "error",
            message:
              error.message || "Failed to complete task. Please try again.",
          });
        }
      } finally {
        this.completing = false;
      }
    },
    formatDateSlashUTC,
  },
};
</script>

<style scoped>
.checklist-modal-content {
  max-height: 70vh;
  overflow-y: auto;
}

.checklist-header-info {
  border-bottom: 1px solid #e0e0e0;
}

.progress-block {
  text-align: center;
  border: 1px solid #e0e0e0;
}

.checklist-items {
  max-height: 50vh;
  overflow-y: auto;
}

.checklist-item {
  transition: background-color 0.2s ease;
}

.checklist-item:hover {
  background-color: #fafafa;
}

.checklist-item.completed {
  background-color: #f1f8e9;
}

.checklist-item.required {
  border-left: 4px solid #ff9800;
}

.checklist-item.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}

.task-description {
  font-size: 15px;
  line-height: 1.5;
  color: #333;
}

.completed-text {
  text-decoration: line-through;
  color: #666 !important;
}
</style>
